import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Search, Edit, Trash2, Filter, Package, CheckCircle, AlertTriangle, XCircle, ArrowLeft, ChevronRight, Grid, List, Eye } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Table from '../../components/Table';
import Modal from '../../components/Modal';
import { apiService } from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';
import {
  getEquipmentStatus,
  getEquipmentStoredStatusLabel,
  getEquipmentStoredStatusColor,
  renderCell,
  coalesce,
  renderCellDate,
} from '../../utils/helpers';
import { EQUIPMENT_TYPES, EQUIPMENT_TYPE_LABELS } from '../../utils/constants';
import EquipmentDetailSummary from '../../components/EquipmentDetailSummary';

const equipmentTypeFilterOptions = [
  { value: '', label: 'Toutes catégories' },
  { value: EQUIPMENT_TYPES.EXTINGUISHER, label: EQUIPMENT_TYPE_LABELS[EQUIPMENT_TYPES.EXTINGUISHER] },
  { value: EQUIPMENT_TYPES.RIA, label: EQUIPMENT_TYPE_LABELS[EQUIPMENT_TYPES.RIA] },
  { value: EQUIPMENT_TYPES.BDI, label: EQUIPMENT_TYPE_LABELS[EQUIPMENT_TYPES.BDI] },
  { value: EQUIPMENT_TYPES.CABINET, label: EQUIPMENT_TYPE_LABELS[EQUIPMENT_TYPES.CABINET] },
];

function renderEquipmentDetailsCell(row) {
  if (row.type === EQUIPMENT_TYPES.EXTINGUISHER) {
    const parts = [row.extinguisherType, row.capacity].filter(Boolean);
    return renderCell(parts.length ? parts.join(' · ') : null);
  }
  const parts = [row.detailedType, row.structure, row.affectation].filter(Boolean);
  return renderCell(parts.length ? parts.join(' · ') : null);
}

const EquipmentManagement = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { siteId } = useParams(); // Get siteId from URL params
  const [equipment, setEquipment] = useState([]);
  const [filteredEquipment, setFilteredEquipment] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState({ open: false, equipment: null });
  const [siteInfo, setSiteInfo] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card' - default to table
  const [viewEquipment, setViewEquipment] = useState(null);

  useEffect(() => {
    loadEquipment();
    if (siteId) {
      loadSiteInfo();
    }
  }, [siteId]);

  useEffect(() => {
    filterEquipment();
  }, [searchQuery, typeFilter, equipment]);

  const loadSiteInfo = async () => {
    try {
      const data = await apiService.getSite(siteId);
      setSiteInfo(data);
    } catch (error) {
      console.error('Failed to load site info:', error);
    }
  };

  const loadEquipment = async () => {
    try {
      setLoading(true);
      const data = await apiService.getEquipment(null, siteId);
      setEquipment(data);
    } catch (error) {
      console.error('Failed to load equipment:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterEquipment = () => {
    let filtered = [...equipment];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((item) => {
        const haystack = [
          item.serialNumber,
          item.type,
          item.clientName,
          item.siteName,
          item.location,
          item.inventoryNumber,
          item.extinguisherType,
          item.detailedType,
          item.capacity,
          item.notes,
          item.physicalCondition,
          EQUIPMENT_TYPE_LABELS[item.type],
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(query);
      });
    }

    if (typeFilter) {
      filtered = filtered.filter((item) => item.type === typeFilter);
    }

    setFilteredEquipment(filtered);
  };

  const handleDelete = async () => {
    try {
      await apiService.deleteEquipment(deleteModal.equipment.id);
      setDeleteModal({ open: false, equipment: null });
      toast.success('Équipement supprimé');
      await loadEquipment();
    } catch (error) {
      console.error('Failed to delete equipment:', error);
      toast.error(error.message || 'Échec de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Calculate stats
  const totalEquipment = equipment.length;
  const operationalEquipment = equipment.filter(eq => getEquipmentStatus(eq) === 'operational').length;
  const maintenanceRequired = equipment.filter(eq => {
    const status = getEquipmentStatus(eq);
    return status === 'expiring_soon' || status === 'maintenance_required';
  }).length;
  const expiredEquipment = equipment.filter(eq => getEquipmentStatus(eq) === 'expired').length;

  const addEquipmentQuery =
    siteId && siteInfo?.clientId
      ? `?clientId=${siteInfo.clientId}&siteId=${siteId}`
      : '';

  const returnToPath = siteId ? `/admin/sites/${siteId}/equipment` : '/equipment';

  const renderRowActions = (row) => (
    <div className="flex items-center justify-center gap-2">
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setViewEquipment(row)}
        className="p-2.5 text-gray-700 hover:bg-gray-100 rounded-xl transition-all hover:shadow-md"
        title="Voir la fiche complète"
      >
        <Eye className="w-4 h-4" />
      </motion.button>
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() =>
          navigate(
            `/equipment/edit/${row.id}?returnTo=${encodeURIComponent(returnToPath)}`
          )
        }
        className="p-2.5 text-blue-600 hover:bg-blue-50 rounded-xl transition-all hover:shadow-md"
        title="Modifier"
      >
        <Edit className="w-4 h-4" />
      </motion.button>
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setDeleteModal({ open: true, equipment: row })}
        className="p-2.5 text-red-600 hover:bg-red-50 rounded-xl transition-all hover:shadow-md"
        title="Supprimer"
      >
        <Trash2 className="w-4 h-4" />
      </motion.button>
    </div>
  );

  const clientColumn = {
    header: 'Client',
    accessor: 'clientName',
    render: (row) => (
      <span className="text-sm text-gray-700">{renderCell(row.clientName)}</span>
    ),
  };

  const siteColumn = {
    header: 'Site',
    accessor: 'siteName',
    render: (row) => (
      <span className="text-sm text-gray-700">
        {renderCell(coalesce(row.siteName, 'Site Principal'))}
      </span>
    ),
  };

  const storedStatusColumn = {
    header: 'Statut',
    accessor: 'status',
    size: 'md',
    render: (row) => {
      const stored = row.status || 'operational';
      return (
        <span
          className={`inline-block whitespace-nowrap px-3 py-1 rounded-full text-xs font-medium ${getEquipmentStoredStatusColor(stored)}`}
        >
          {getEquipmentStoredStatusLabel(stored)}
        </span>
      );
    },
  };

  const actionsColumn = {
    header: 'Actions',
    accessor: 'id',
    size: 'actions',
    actionCount: 3,
    render: (row) => renderRowActions(row),
  };

  const listColumns = [
    ...(!siteId ? [clientColumn, siteColumn] : []),
    {
      header: 'Catégorie',
      accessor: 'type',
      size: 'sm',
      render: (row) => (
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-800">
          <span aria-hidden>{row.type === EQUIPMENT_TYPES.EXTINGUISHER ? '🧯' : '📦'}</span>
          {renderCell(EQUIPMENT_TYPE_LABELS[row.type] || row.type)}
        </span>
      ),
    },
    {
      header: 'N° série',
      accessor: 'serialNumber',
      size: 'md',
      render: (row) => (
        <span className="text-sm font-semibold text-gray-900">{renderCell(row.serialNumber)}</span>
      ),
    },
    {
      header: 'Détails',
      accessor: 'details',
      render: (row) => (
        <span className="text-sm text-gray-700 line-clamp-2">{renderEquipmentDetailsCell(row)}</span>
      ),
    },
    {
      header: 'Emplacement',
      accessor: 'location',
      render: (row) => (
        <span className="text-sm text-gray-700 line-clamp-2">
          {renderCell(coalesce(row.location, row.detailedLocation))}
        </span>
      ),
    },
    {
      header: 'Prochaine vérif.',
      accessor: 'nextMaintenanceDate',
      size: 'sm',
      render: (row) => (
        <span className="text-sm text-gray-700 whitespace-nowrap">
          {renderCellDate(row.nextMaintenanceDate, 'dd/MM/yyyy')}
        </span>
      ),
    },
    storedStatusColumn,
    actionsColumn,
  ];

  const renderEquipmentCard = (equip, index) => {
    const isExtinguisher = equip.type === EQUIPMENT_TYPES.EXTINGUISHER;
    const storedStatus = equip.status || 'operational';
    return (
      <motion.div
        key={equip.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05 }}
      >
        <Card className="hover:shadow-lg transition-shadow">
          <div className="space-y-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="text-3xl">{isExtinguisher ? '🧯' : '📦'}</div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {isExtinguisher
                      ? coalesce(equip.detailedType, equip.extinguisherType, 'Extincteur')
                      : EQUIPMENT_TYPE_LABELS[equip.type]}
                  </h3>
                  <p className="text-sm text-gray-500">{equip.serialNumber}</p>
                </div>
              </div>
              <span
                className={`px-2 py-1 text-xs font-semibold rounded-full ${getEquipmentStoredStatusColor(storedStatus)}`}
              >
                {getEquipmentStoredStatusLabel(storedStatus)}
              </span>
            </div>

            <div className="max-h-64 overflow-y-auto text-sm border-t border-gray-100 pt-3">
              <EquipmentDetailSummary equipment={equip} columns={1} dense />
            </div>

            <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
              <Button
                variant="outline"
                size="sm"
                icon={Eye}
                onClick={() => setViewEquipment(equip)}
                className="flex-1"
              >
                Voir
              </Button>
              <Button
                variant="outline"
                size="sm"
                icon={Edit}
                onClick={() =>
                  navigate(
                    `/equipment/edit/${equip.id}?returnTo=${encodeURIComponent(returnToPath)}`
                  )
                }
              />
              <Button
                variant="outline"
                size="sm"
                icon={Trash2}
                onClick={() => setDeleteModal({ open: true, equipment: equip })}
                className="text-red-600 hover:bg-red-50"
              />
            </div>
          </div>
        </Card>
      </motion.div>
    );
  };

  const renderSectionEmpty = (message) => (
    <p className="text-sm text-gray-500 py-6 text-center">{message}</p>
  );

  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-6">
      {/* Breadcrumb Navigation */}
      {siteId && siteInfo && (
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <button 
            onClick={() => navigate('/clients')}
            className="hover:text-red-600 transition-colors"
          >
            Clients
          </button>
          <ChevronRight className="w-4 h-4" />
          <button 
            onClick={() => navigate(`/clients/${siteInfo.clientId}/sites`)}
            className="hover:text-red-600 transition-colors"
          >
            {siteInfo.clientName}
          </button>
          <ChevronRight className="w-4 h-4" />
          <span className="font-medium text-gray-900">{siteInfo.name}</span>
        </div>
      )}

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4"
      >
        <div>
          {siteId && (
            <Button
              variant="outline"
              icon={ArrowLeft}
              onClick={() => siteInfo ? navigate(`/clients/${siteInfo.clientId}/sites`) : navigate('/clients')}
              className="mb-3"
              size="sm"
            >
              Retour aux sites
            </Button>
          )}
          <h1 className="text-xl sm:text-3xl md:text-4xl font-extrabold text-gray-900">
            {siteId && siteInfo ? `Équipements - ${siteInfo.name}` : 'Gestion des Équipements'}
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2 font-medium">
            {siteId && siteInfo ? `${siteInfo.clientName}` : 'Gérez tous vos équipements de sécurité incendie'}
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap w-full sm:w-auto">
          <Button
            variant={viewMode === 'table' ? 'primary' : 'outline'}
            size="sm"
            icon={List}
            onClick={() => setViewMode('table')}
          >
            Tableau
          </Button>
          <Button
            variant={viewMode === 'card' ? 'primary' : 'outline'}
            size="sm"
            icon={Grid}
            onClick={() => setViewMode('card')}
          >
            Cartes
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/equipment/add-extinguisher${addEquipmentQuery}`)}
            icon={Plus}
            className="flex-1 sm:flex-none"
          >
            Extincteur
          </Button>
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => navigate(`/equipment/add-other${addEquipmentQuery}`)}
            className="flex-1 sm:flex-none"
          >
            Autre Équipement
          </Button>
        </div>
      </motion.div>

      {/* Stats Bar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-red-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-red-500 to-red-600 rounded-lg sm:rounded-xl shadow-lg shadow-red-500/30">
                <Package className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Total Équipements</p>
            <p className="text-xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{totalEquipment}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-green-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg sm:rounded-xl shadow-lg shadow-green-500/30">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-xs sm:text-sm font-bold text-green-600 bg-green-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg">
                {totalEquipment > 0 ? Math.round((operationalEquipment / totalEquipment) * 100) : 0}%
              </span>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Terminés</p>
            <p className="text-xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{operationalEquipment}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-amber-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg sm:rounded-xl shadow-lg shadow-amber-500/30">
                <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-xs sm:text-sm font-bold text-amber-600 bg-amber-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg">
                {totalEquipment > 0 ? Math.round((maintenanceRequired / totalEquipment) * 100) : 0}%
              </span>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Maintenance</p>
            <p className="text-xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{maintenanceRequired}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          whileHover={{ y: -3, scale: 1.02 }}
          className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-100 p-3 sm:p-4 hover:shadow-2xl hover:border-rose-200 transition-all"
        >
          <div>
            <div className="flex items-start justify-between mb-2 sm:mb-3">
              <div className="p-2 sm:p-2.5 bg-gradient-to-br from-rose-500 to-rose-600 rounded-lg sm:rounded-xl shadow-lg shadow-rose-500/30">
                <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-xs sm:text-sm font-bold text-rose-600 bg-rose-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg">
                {totalEquipment > 0 ? Math.round((expiredEquipment / totalEquipment) * 100) : 0}%
              </span>
            </div>
            <p className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Expirés</p>
            <p className="text-xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 leading-none">{expiredEquipment}</p>
          </div>
        </motion.div>
      </div>

      {/* Search and Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <div className="flex-1">
              <Input
                placeholder="Rechercher (n° série, client, site, type…)…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                icon={Search}
              />
            </div>
            <div className="sm:w-64">
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                options={equipmentTypeFilterOptions}
              />
            </div>
            <Button variant="outline" icon={Filter} className="sm:w-auto">
              Filtres
            </Button>
          </div>
        </Card>
      </motion.div>

      {/* Liste unifiée */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="p-4 sm:p-6">
          {viewMode === 'table' &&
            (filteredEquipment.length > 0 ? (
              <Table columns={listColumns} data={filteredEquipment} />
            ) : (
              renderSectionEmpty(
                searchQuery || typeFilter
                  ? 'Aucun équipement ne correspond à votre recherche.'
                  : 'Aucun équipement. Utilisez « + Extincteur » ou « + Autre Équipement ».'
              )
            ))}

          {viewMode === 'card' &&
            (filteredEquipment.length > 0 ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {filteredEquipment.map((equip, index) => renderEquipmentCard(equip, index))}
              </div>
            ) : (
              renderSectionEmpty('Aucun équipement à afficher.')
            ))}
        </Card>
      </motion.div>

      {/* Delete Modal */}
      <Modal
        isOpen={!!viewEquipment}
        onClose={() => setViewEquipment(null)}
        title="Fiche équipement complète"
      >
        {viewEquipment && (
          <div className="max-h-[70vh] overflow-y-auto">
            <EquipmentDetailSummary equipment={viewEquipment} columns={2} dense />
          </div>
        )}
      </Modal>

      <Modal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, equipment: null })}
        title="Supprimer l'équipement"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Êtes-vous sûr de vouloir supprimer l'équipement{' '}
            <strong>{deleteModal.equipment?.serialNumber}</strong> ? Cette action est irréversible.
          </p>
          <div className="flex items-center justify-end space-x-3">
            <Button
              variant="secondary"
              onClick={() => setDeleteModal({ open: false, equipment: null })}
            >
              Annuler
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Supprimer
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EquipmentManagement;

